import {
  type GetFrigateDashboardOrderQuery,
  type GetFrigateDashboardOrderQueryVariables,
  useGetFrigateDashboardOrderQuery,
} from '../../../../api'
import { type EagerResponse, useApolloRequestWrapper } from '../../../api'
import { type FrigateDashboardOrderResponse, type FrigateDashboardOrder } from './types'

function mapResponse(data: GetFrigateDashboardOrderQuery): FrigateDashboardOrderResponse {
  const gqlDashboardOrder =
    data.instacart_enterprise_frigate_fulfillment_v1_DashboardOrderService_GetDashboardOrder!
      .dashboardOrder!

  const dashboardOrder: FrigateDashboardOrder = {
    orderId: gqlDashboardOrder.orderId!,
    userId: gqlDashboardOrder.userId!,
    externalId: gqlDashboardOrder.externalId!,
    timezone: gqlDashboardOrder.timezone!,
    customerContext: {
      clientConfigurationId: gqlDashboardOrder.customerContext!.clientConfigurationId!,
      storeConfigurationId: gqlDashboardOrder.customerContext!.storeConfigurationId!,
      countryId: gqlDashboardOrder.customerContext!.countryId!,
    },
    customerInformation: {
      fullName: gqlDashboardOrder.customerInformation!.fullName!,
      firstName: gqlDashboardOrder.customerInformation!.firstName!,
      lastName: gqlDashboardOrder.customerInformation!.lastName!,
      phoneNumber: gqlDashboardOrder.customerInformation!.phoneNumber!,
      email: gqlDashboardOrder.customerInformation!.email!,
    },
    addressInformation: {
      streetName: gqlDashboardOrder.addressInformation!.streetName!,
      unitNumber: gqlDashboardOrder.addressInformation!.unitNumber!,
      city: gqlDashboardOrder.addressInformation!.city!,
      state: gqlDashboardOrder.addressInformation!.state!,
      zipCode: gqlDashboardOrder.addressInformation!.zipCode!,
    },
    fulfillmentDetails: {
      retailerId: gqlDashboardOrder.fulfillmentDetails!.retailerId!,
      retailerLocationId: gqlDashboardOrder.fulfillmentDetails!.retailerLocationId!,
      retailerLocationName: gqlDashboardOrder.fulfillmentDetails!.retailerLocationName!,
      isAlcoholic: gqlDashboardOrder.fulfillmentDetails!.isAlcoholic!,
      serviceOptionId: gqlDashboardOrder.fulfillmentDetails!.serviceOptionId!,
      addressId: gqlDashboardOrder.fulfillmentDetails!.addressId!,
      previouslyRescheduled: gqlDashboardOrder.fulfillmentDetails!.previouslyRescheduled!,
      windowStartsAt: gqlDashboardOrder.fulfillmentDetails!.windowStartsAt!,
      windowEndsAt: gqlDashboardOrder.fulfillmentDetails!.windowEndsAt!,
      numOfBags: gqlDashboardOrder.fulfillmentDetails!.numOfBags!,
      estimatedPickupTime: gqlDashboardOrder.fulfillmentDetails!.estimatedPickupTime!,
      estimatedDeliveryTime: gqlDashboardOrder.fulfillmentDetails!.estimatedDeliveryTime!,
      retailerProvidedAmounts: {
        bagLabel: gqlDashboardOrder.fulfillmentDetails!.retailerProvidedAmounts!.bagLabel!,
      },
    },
    shopperInformation: {
      firstName: gqlDashboardOrder.shopperInformation!.firstName!,
      avatarUrl: gqlDashboardOrder.shopperInformation!.avatarUrl!,
      carModelAndColor: gqlDashboardOrder.shopperInformation!.carModelAndColor!,
    },
    orderDeliveryPhotoList: {
      areOrderDeliveryPhotosExpired:
        gqlDashboardOrder.orderDeliveryPhotoList!.areOrderDeliveryPhotosExpired!,
      results:
        gqlDashboardOrder.orderDeliveryPhotoList!.results?.map(photo => ({
          photoUrl: photo!.photoUrl!,
        })) ?? [],
    },
    orderHistoryEvents:
      gqlDashboardOrder.orderHistoryEvents?.map(event => ({
        action: event?.action?.toString() ?? '',
        actionedAt: event!.actionedAt!,
        windowStartsAt: event!.windowStartsAt!,
        windowEndsAt: event!.windowEndsAt!,
      })) ?? [],
    latestTipFormatted: gqlDashboardOrder.latestTipFormatted!,
    orderState: gqlDashboardOrder.orderState!,
    loyaltyInformation: {
      loyaltyIdentifier: gqlDashboardOrder.loyaltyInformation!.loyaltyIdentifier!,
      loyaltyCardId: gqlDashboardOrder.loyaltyInformation!.loyaltyCardId!,
    },
  }

  return {
    dashboardOrder,
    loyaltyInformation: dashboardOrder.loyaltyInformation,
  }
}

export const useGetFrigateDashboardOrder = (
  variables: GetFrigateDashboardOrderQueryVariables
): EagerResponse<FrigateDashboardOrderResponse> => {
  const shouldSkip =
    !variables?.input?.partnerId ||
    !variables?.input?.retailerId ||
    (!variables?.input?.orderDeliveryId && !variables?.input?.userId)

  const query = useGetFrigateDashboardOrderQuery({
    variables,
    skip: shouldSkip,
    refetchWritePolicy: 'overwrite',
  })

  return useApolloRequestWrapper({ query, mapResponse })
}
